import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

import { BaseService } from './base/base.service';
import { AppConfig } from './app-config';

import {
  Pba,
  CreatePbaPayload,
  BaseStudy,
  TestedParty,
  AddPbaTransactionPayload,
  PbaTransaction,
  Entity,
  Characterization,
  FunctionPerformed,
  TypeOfPrimaryFunctionOption,
  Pli,
  PliValueType,
  BaseCountry,
  CharacterizeTestedPartyDescription,
  CharacterizeTestedPartyFunctionsPerformed,
  CharacterizeTestedPartyCodes,
  MajorServiceClassification
} from '../models';
import { ObjectResponse, PagedResponse, ListResponse } from '../interfaces/response';

@Injectable()
export class PbasService extends BaseService<Pba> {
  private _checkIfCompSearchTabIsEnabled = new Subject();
  public checkIfCompSearchTabIsEnabled$ = this._checkIfCompSearchTabIsEnabled.asObservable();

  // TODO: find a way to split to separate services for PBA, TestedParty and Characterization
  constructor(http: HttpClient, protected config: AppConfig) {
    super(http, config);
  }

  public listForStudy(studyId: number, filter?: string): Observable<PagedResponse<Pba>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}study/${studyId}`);

    return this.http.get<PagedResponse<Pba>>(apiUrl);
  }

  // used for getting transactions for expansion row in PBAs list
  public listTransactions(id: number, filter?: string): Observable<ListResponse<PbaTransaction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}${id}/transactions`);

    return this.http.get<PagedResponse<PbaTransaction>>(apiUrl);
  }

  public listPrimaryEntitiesForFilter(studyId: number, filter?: string): Observable<ListResponse<Entity>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}study/${studyId}/primary-entities/filter`);

    return this.http.get<ListResponse<Entity>>(apiUrl);
  }

  public createPba(data: CreatePbaPayload): Observable<ObjectResponse<Pba>> {
    return this.http.post<ObjectResponse<Pba>>(`${this.getResourceUrl()}`, data);
  }

  public getListOfPrimaryEntitiesForCreate({ studyId }: BaseStudy): Observable<ListResponse<Entity>> {
    return this.http.get<ListResponse<Entity>>(`${this.getResourceUrl()}study/${studyId}/primary-entities/create`);
  }

  public getTestedParty(pbaId: number): Observable<ObjectResponse<TestedParty>> {
    return this.http.get<ObjectResponse<TestedParty>>(`${this.getResourceUrl()}${pbaId}/tested-party`);
  }

  public listMajorServiceClassificationsForTestedParty(pbaId: number):
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.get<ObjectResponse<MajorServiceClassification[]>>(
      `${this.getResourceUrl()}${pbaId}/tested-party/major-service-classifications`
    );
  }

  public listAllMajorServiceClassifications():
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.get<ObjectResponse<MajorServiceClassification[]>>(`${this.baseUrl}major-service-classifications`);
  }

  public deleteMajorServiceClassificationsForTestedParty(pbaId: number, mscId: number):
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.delete<ObjectResponse<MajorServiceClassification[]>>(
      `${this.getResourceUrl()}${pbaId}/tested-party/major-service-classifications/${mscId}`
    );
  }

  public deleteMinorServiceClassificationsForTestedParty(pbaId: number, mscId: number):
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.delete<ObjectResponse<MajorServiceClassification[]>>(
      `${this.getResourceUrl()}${pbaId}/tested-party/minor-service-classifications/${mscId}`
    );
  }

  public addMajorServiceClassificationsForTestedParty(pbaId: number, mscId: number):
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.post<ObjectResponse<MajorServiceClassification[]>>(
      `${this.getResourceUrl()}${pbaId}/tested-party/major-service-classifications/${mscId}`, {}
    );
  }

  public addMinorServiceClassificationsForTestedParty(pbaId: number, mscId: number):
    Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this.http.post<ObjectResponse<MajorServiceClassification[]>>(
      `${this.getResourceUrl()}${pbaId}/tested-party/minor-service-classifications/${mscId}`, {}
    );
  }

  public getAssociatedTransactions(pbaId: number, filter: string): Observable<PagedResponse<PbaTransaction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}${pbaId}/transactions`);

    return this.http.get<PagedResponse<PbaTransaction>>(apiUrl);
  }

  public createTestedParty({ pbaId }: Pba, data: TestedParty): Observable<ObjectResponse<TestedParty>> {
    return this.http.post<ObjectResponse<TestedParty>>(`${this.getResourceUrl()}${pbaId}/tested-party`, data);
  }

  public editTestedParty({ pbaId }: Pba, data: TestedParty): Observable<ObjectResponse<TestedParty>> {
    return this.http.patch<ObjectResponse<TestedParty>>(`${this.getResourceUrl()}${pbaId}/tested-party`, data);
  }

  public saveTestedParty(pba: Pba, data: TestedParty, isUpdate = false): Observable<ObjectResponse<TestedParty>> {
    return isUpdate ? this.editTestedParty(pba, data) : this.createTestedParty(pba, data);
  }

  public getSegmentTypeLabel(segmentTypeString: string): string {
    return `tested_party_segment.${segmentTypeString.split(/(?=[A-Z])/).join('_').toLowerCase()}`;
  }

  public addTransaction(pbaId: number, payload: AddPbaTransactionPayload): Observable<ObjectResponse<Pba>> {
    return this.http.put<ObjectResponse<Pba>>(`${this.getResourceUrl()}${pbaId}/transactions`, payload);
  }

  public removeTransaction(pbaId: number, transactionId: number): Observable<ObjectResponse<Pba>> {
    return this.http.delete<ObjectResponse<Pba>>(`${this.getResourceUrl()}${pbaId}/transactions/${transactionId}`);
  }

  public listSuitableTransactions(pbaId: number, filter?: string): Observable<PagedResponse<PbaTransaction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}${pbaId}/transactions/suitable`);

    return this.http.get<PagedResponse<PbaTransaction>>(apiUrl);
  }

  public getCharacterization(pbaId: number): Observable<ObjectResponse<Characterization>> {
    return this.http.get<ObjectResponse<Characterization>>(`${this.getResourceUrl()}${pbaId}/characterization`);
  }

  public getCharacterizationFunctionsPerformed(primaryFunctionId: number): Observable<ListResponse<FunctionPerformed>> {
    const apiUrl = `${this.getResourceUrl()}characterization/functions-performed/${primaryFunctionId}`;
    return this.http.get<ListResponse<FunctionPerformed>>(apiUrl);
  }

  public saveTestedPartyCharacterizationDescription(
    pbaId: number,
    data: CharacterizeTestedPartyDescription
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/description`;
    return this.http.post<ObjectResponse<Characterization>>(apiUrl, data);
  }

  public updateTestedPartyCharacterizationDescription(
    pbaId: number,
    data: CharacterizeTestedPartyDescription
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/description`;
    return this.http.patch<ObjectResponse<Characterization>>(apiUrl, data);
  }

  public saveTestedPartyCharacterizationFeatures(
    pbaId: number,
    data: CharacterizeTestedPartyFunctionsPerformed
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/functions-performed`;
    return this.http.post<ObjectResponse<Characterization>>(apiUrl, data);
  }

  public updateTestedPartyCharacterizationFeatures(
    pbaId: number,
    data: CharacterizeTestedPartyFunctionsPerformed
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/functions-performed`;
    return this.http.patch<ObjectResponse<Characterization>>(apiUrl, data);
  }

  public saveTestedPartyCharacterizationOptionType(
    pbaId: number,
    data: CharacterizeTestedPartyCodes
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/option-type`;
    return this.http.post<ObjectResponse<Characterization>>(apiUrl, data);
  }

  public updateTestedPartyCharacterizationOptionType(
    pbaId: number,
    data: CharacterizeTestedPartyCodes
  ): Observable<ObjectResponse<Characterization>> {
    const apiUrl = `${this.getResourceUrl()}${pbaId}/characterization/option-type`;
    return this.http.patch<ObjectResponse<Characterization>>(apiUrl, data);
  }

  // TODO: override, remove when base updated
  public show(id: number): Observable<ObjectResponse<Pba>> {
    return this.http.get<ObjectResponse<Pba>>(`${this.getResourceUrl()}${id}`);
  }

  public getTypeOfOptions(primaryFunctionId: number): Observable<ListResponse<TypeOfPrimaryFunctionOption>> {
    const apiUrl = `${this.getResourceUrl()}characterization/type-of-options/${primaryFunctionId}`;
    return this.http.get<ListResponse<TypeOfPrimaryFunctionOption>>(apiUrl);
  }

  public saveTestedPartyCharacterization(
    pbaId: number,
    data: Characterization
  ): Observable<ObjectResponse<Characterization>> {
    return this.http.post<ObjectResponse<Characterization>>(`${this.getResourceUrl()}${pbaId}/characterization`, data);
  }

  public updateTestedPartyCharacterization(
    pbaId: number,
    data: Characterization
  ): Observable<ObjectResponse<Characterization>> {
    return this.http.patch<ObjectResponse<Characterization>>(`${this.getResourceUrl()}${pbaId}/characterization`, data);
  }

  public getTestedPartyPli(pbaId: number): Observable<ObjectResponse<Pli>> {
    return this.http.get<ObjectResponse<Pli>>(`${this.getResourceUrl()}${pbaId}/tested-party/pli`);
  }

  public getWatchedPlisList(pbaId: number): Observable<ListResponse<PliValueType>> {
    return this.http.get<ListResponse<PliValueType>>(`${this.getResourceUrl()}${pbaId}/tested-party/watched-plis`);
  }

  public getAssociatedJurisdictions(pbaId: number): Observable<ListResponse<BaseCountry>> {
    return this.http.get<any>(`${this.getResourceUrl()}${pbaId}/associate-jurisdictions`);
  }

  public checkIfCompSearchTabIsEnabled(): void {
    this._checkIfCompSearchTabIsEnabled.next();
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}pbas/`;
  }
}
